/** Inline GLSL sources for GPU effect passes. */

export const VERTEX_FULLSCREEN = `#version 300 es
precision highp float;
in vec2 a_position;
out vec2 v_uv;
void main() {
  v_uv = a_position * 0.5 + 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

export const FRAG_COPY = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_texture;
out vec4 outColor;
void main() {
  outColor = texture(u_texture, v_uv);
}
`;

export const FRAG_COLOR_GRADE = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_texture;
uniform float u_contrast;
uniform float u_saturation;
uniform float u_temperature;
uniform float u_shadows;
uniform float u_highlights;
uniform float u_exposure;
out vec4 outColor;

vec3 applyTemperature(vec3 c, float t) {
  c.r += t * 0.15;
  c.b -= t * 0.15;
  return c;
}

vec3 applyShadowsHighlights(vec3 c, float shadows, float highlights) {
  float lum = dot(c, vec3(0.299, 0.587, 0.114));
  float sMask = 1.0 - smoothstep(0.0, 0.45, lum);
  float hMask = smoothstep(0.55, 1.0, lum);
  c += shadows * sMask * 0.25;
  c += highlights * hMask * 0.2;
  return c;
}

void main() {
  vec4 src = texture(u_texture, v_uv);
  vec3 c = src.rgb * pow(2.0, u_exposure);
  c = (c - 0.5) * u_contrast + 0.5;
  float l = dot(c, vec3(0.299, 0.587, 0.114));
  c = mix(vec3(l), c, u_saturation);
  c = applyTemperature(c, u_temperature);
  c = applyShadowsHighlights(c, u_shadows, u_highlights);
  outColor = vec4(clamp(c, 0.0, 1.0), src.a);
}
`;

export const FRAG_BRIGHT_THRESHOLD = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_texture;
uniform float u_threshold;
out vec4 outColor;
void main() {
  vec3 c = texture(u_texture, v_uv).rgb;
  float lum = max(c.r, max(c.g, c.b));
  float mask = smoothstep(u_threshold, u_threshold + 0.08, lum);
  outColor = vec4(c * mask, mask);
}
`;

export const FRAG_BLUR_H = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_texture;
uniform vec2 u_texel;
uniform float u_radius;
out vec4 outColor;
void main() {
  vec4 sum = vec4(0.0);
  float wsum = 0.0;
  int r = int(u_radius);
  for (int i = -16; i <= 16; i++) {
    if (abs(i) > r) continue;
    float w = exp(-float(i*i) / (2.0 * float(r*r + 1)));
    vec2 off = vec2(float(i) * u_texel.x, 0.0);
    sum += texture(u_texture, v_uv + off) * w;
    wsum += w;
  }
  outColor = sum / wsum;
}
`;

export const FRAG_BLUR_V = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_texture;
uniform vec2 u_texel;
uniform float u_radius;
out vec4 outColor;
void main() {
  vec4 sum = vec4(0.0);
  float wsum = 0.0;
  int r = int(u_radius);
  for (int i = -16; i <= 16; i++) {
    if (abs(i) > r) continue;
    float w = exp(-float(i*i) / (2.0 * float(r*r + 1)));
    vec2 off = vec2(0.0, float(i) * u_texel.y);
    sum += texture(u_texture, v_uv + off) * w;
    wsum += w;
  }
  outColor = sum / wsum;
}
`;

export const FRAG_BLOOM_COMPOSITE = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_base;
uniform sampler2D u_bloom;
uniform float u_intensity;
out vec4 outColor;
void main() {
  vec4 base = texture(u_base, v_uv);
  vec3 bloom = texture(u_bloom, v_uv).rgb;
  outColor = vec4(base.rgb + bloom * u_intensity, base.a);
}
`;

export const FRAG_MOTION_BLUR = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_current;
uniform sampler2D u_prev;
uniform float u_strength;
uniform int u_sampleCount;
out vec4 outColor;
void main() {
  vec4 cur = texture(u_current, v_uv);
  vec4 acc = cur;
  float wsum = 1.0;
  for (int i = 1; i < 16; i++) {
    if (i >= u_sampleCount) break;
    float t = float(i) / float(u_sampleCount);
    vec2 off = vec2(t * u_strength * 0.02, 0.0);
    acc += texture(u_prev, v_uv - off);
    wsum += 1.0;
  }
  outColor = acc / wsum;
}
`;

export const FRAG_LENS_FLARE = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_texture;
uniform float u_intensity;
uniform float u_scale;
uniform vec2 u_position;
uniform float u_streakLength;
out vec4 outColor;

float ghost(vec2 uv, vec2 center, float size) {
  float d = length(uv - center);
  return exp(-d * d / max(1e-5, size * size));
}

void main() {
  vec4 base = texture(u_texture, v_uv);
  vec2 uv = v_uv;
  vec2 light = u_position;
  float lum = dot(base.rgb, vec3(0.299, 0.587, 0.114));
  float bright = mix(0.55, 1.0, smoothstep(0.45, 0.95, lum));

  float halo = ghost(uv, light, 0.14 * u_scale);
  float ring = abs(length(uv - light) - 0.18 * u_scale);
  float iris = exp(-ring * ring * 900.0) * 0.65;

  // Anamorphic horizontal streak
  float ax = abs(uv.x - light.x);
  float ay = abs(uv.y - light.y);
  float streak = exp(-ay * ay / (0.0009 * u_scale * u_scale))
    * exp(-ax / max(0.08, u_streakLength * 0.35))
    * 1.1;

  vec3 flare = vec3(0.0);
  flare += vec3(1.0, 0.92, 0.75) * (halo * 1.2 + iris);
  flare += vec3(0.75, 0.9, 1.0) * streak * 0.85;

  // Hex-ish ghost orbs along optical axis
  for (int i = 1; i <= 6; i++) {
    float t = float(i) / 6.0;
    vec2 gpos = mix(light, vec2(1.0) - light, t * 0.85);
    float g = ghost(uv, gpos, (0.02 + 0.015 * float(i)) * u_scale);
    vec3 tint = mix(vec3(1.0, 0.7, 0.4), vec3(0.5, 0.75, 1.0), t);
    flare += tint * g * (0.55 - t * 0.25);
  }

  flare *= u_intensity * bright;
  outColor = vec4(base.rgb + flare, base.a);
}
`;

export const FRAG_FILM_GRAIN = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_texture;
uniform float u_amount;
uniform float u_size;
uniform float u_time;
out vec4 outColor;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

void main() {
  vec4 base = texture(u_texture, v_uv);
  float scale = mix(900.0, 280.0, clamp(u_size, 0.0, 1.0));
  vec2 gp = floor(v_uv * scale);
  float n = hash(gp + floor(u_time * 24.0));
  float grain = (n - 0.5) * u_amount * 0.55;
  // Luma-weighted grain (more visible in midtones)
  float luma = dot(base.rgb, vec3(0.299, 0.587, 0.114));
  float mid = smoothstep(0.08, 0.35, luma) * (1.0 - smoothstep(0.7, 0.98, luma));
  vec3 col = base.rgb + grain * (0.45 + mid);
  outColor = vec4(clamp(col, 0.0, 1.0), base.a);
}
`;

export const FRAG_VIGNETTE = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_texture;
uniform float u_amount;
uniform float u_softness;
uniform float u_roundness;
out vec4 outColor;

void main() {
  vec4 base = texture(u_texture, v_uv);
  vec2 p = v_uv * 2.0 - 1.0;
  p.x *= mix(1.0, 1.25, 1.0 - u_roundness);
  float d = length(p);
  float soft = mix(0.15, 0.85, clamp(u_softness, 0.0, 1.0));
  float vig = smoothstep(1.15, 1.15 - soft, d);
  float dark = mix(1.0, vig, clamp(u_amount, 0.0, 1.5));
  outColor = vec4(base.rgb * dark, base.a);
}
`;

export const FRAG_CHROMATIC_ABERRATION = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_texture;
uniform float u_amount;
uniform float u_radial;
out vec4 outColor;

void main() {
  vec2 c = v_uv - 0.5;
  float dist = length(c);
  float strength = u_amount * 0.012 * mix(1.0, dist * 2.2, clamp(u_radial, 0.0, 1.0));
  vec2 dir = normalize(c + vec2(0.0001));
  float r = texture(u_texture, v_uv + dir * strength).r;
  float g = texture(u_texture, v_uv).g;
  float b = texture(u_texture, v_uv - dir * strength).b;
  outColor = vec4(r, g, b, 1.0);
}
`;

export const FRAG_ANAMORPHIC_STREAK = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_texture;
uniform float u_intensity;
uniform float u_width;
uniform float u_positionY;
uniform float u_hue;
out vec4 outColor;

vec3 hsl2rgb(float h, float s, float l) {
  float c = (1.0 - abs(2.0 * l - 1.0)) * s;
  float x = c * (1.0 - abs(mod(h * 6.0, 2.0) - 1.0));
  float m = l - c * 0.5;
  vec3 rgb;
  if (h < 1.0/6.0) rgb = vec3(c, x, 0.0);
  else if (h < 2.0/6.0) rgb = vec3(x, c, 0.0);
  else if (h < 3.0/6.0) rgb = vec3(0.0, c, x);
  else if (h < 4.0/6.0) rgb = vec3(0.0, x, c);
  else if (h < 5.0/6.0) rgb = vec3(x, 0.0, c);
  else rgb = vec3(c, 0.0, x);
  return rgb + m;
}

void main() {
  vec4 base = texture(u_texture, v_uv);
  float ay = abs(v_uv.y - u_positionY);
  float streak = exp(-ay * ay / max(1e-5, u_width * u_width * 0.015));
  float edgeFade = smoothstep(0.0, 0.12, v_uv.x) * smoothstep(1.0, 0.88, v_uv.x);
  float luma = dot(base.rgb, vec3(0.299, 0.587, 0.114));
  float boost = smoothstep(0.55, 0.95, luma) * 0.7 + 0.3;
  vec3 tint = hsl2rgb(fract(u_hue), 0.55, 0.72);
  vec3 add = tint * streak * edgeFade * u_intensity * boost;
  outColor = vec4(base.rgb + add, base.a);
}
`;

export const FRAG_DREAM_HAZE = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_texture;
uniform float u_amount;
uniform float u_bloom;
uniform float u_lift;
out vec4 outColor;

void main() {
  vec4 base = texture(u_texture, v_uv);
  vec3 soft = vec3(0.0);
  soft += texture(u_texture, v_uv + vec2(0.003, 0.0)).rgb;
  soft += texture(u_texture, v_uv + vec2(-0.003, 0.0)).rgb;
  soft += texture(u_texture, v_uv + vec2(0.0, 0.003)).rgb;
  soft += texture(u_texture, v_uv + vec2(0.0, -0.003)).rgb;
  soft += texture(u_texture, v_uv + vec2(0.002, 0.002)).rgb;
  soft += texture(u_texture, v_uv + vec2(-0.002, -0.002)).rgb;
  soft *= 1.0 / 6.0;
  float luma = dot(base.rgb, vec3(0.299, 0.587, 0.114));
  vec3 bloom = soft * (0.65 + luma * u_bloom);
  vec3 mist = mix(base.rgb, bloom, clamp(u_amount, 0.0, 1.0));
  mist = mist * (1.0 - u_lift * 0.15) + vec3(u_lift * 0.12);
  outColor = vec4(clamp(mist, 0.0, 1.0), base.a);
}
`;

export const FRAG_GLITCH = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_texture;
uniform float u_intensity;
uniform float u_rgbOffset;
uniform float u_noiseAmount;
uniform float u_scanLineOpacity;
uniform float u_seed;
uniform float u_time;
out vec4 outColor;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7)) + u_seed) * 43758.5453);
}

void main() {
  vec2 uv = v_uv;
  float block = floor(uv.y * 24.0 + u_seed);
  float jitter = (hash(vec2(block, u_seed)) - 0.5) * u_intensity * 0.04;
  uv.x += jitter;

  float shift = u_rgbOffset * u_intensity * 0.01;
  float r = texture(u_texture, uv + vec2(shift, 0.0)).r;
  float g = texture(u_texture, uv).g;
  float b = texture(u_texture, uv - vec2(shift, 0.0)).b;
  vec3 col = vec3(r, g, b);

  float scan = step(0.5, fract(uv.y * 400.0)) * u_scanLineOpacity * u_intensity;
  col -= scan;

  float n = hash(uv * 1000.0 + u_time) * u_noiseAmount * u_intensity;
  col += n - u_noiseAmount * u_intensity * 0.5;

  outColor = vec4(clamp(col, 0.0, 1.0), 1.0);
}
`;

export const FRAG_ALPHA_COMPOSITE = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_scene;
uniform sampler2D u_effect;
uniform float u_alpha;
uniform int u_additive;
out vec4 outColor;
void main() {
  vec4 scene = texture(u_scene, v_uv);
  vec4 effect = texture(u_effect, v_uv);
  if (u_additive == 1) {
    outColor = vec4(scene.rgb + effect.rgb * u_alpha, 1.0);
  } else {
    outColor = vec4(effect.rgb * u_alpha + scene.rgb * (1.0 - u_alpha), 1.0);
  }
}
`;

export const FRAG_HEAT_DISTORTION = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_texture;
uniform float u_strength;
uniform float u_speed;
uniform float u_time;
out vec4 outColor;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
}

void main() {
  vec2 uv = v_uv;
  float n = noise(uv * 6.0 + vec2(u_time * u_speed, u_time * u_speed * 0.7));
  uv.y += (n - 0.5) * u_strength * 0.04;
  uv.x += (noise(uv * 4.0 + u_time) - 0.5) * u_strength * 0.01;
  outColor = texture(u_texture, uv);
}
`;

export const FRAG_VHS = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_texture;
uniform float u_intensity;
uniform float u_rgbOffset;
uniform float u_noiseAmount;
uniform float u_scanLineOpacity;
uniform float u_blur;
uniform float u_seed;
uniform float u_time;
out vec4 outColor;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7)) + u_seed) * 43758.5453);
}

void main() {
  vec2 uv = v_uv;
  float wobble = sin(uv.y * 80.0 + u_time * 3.0) * u_intensity * 0.003;
  uv.x += wobble;
  float shift = u_rgbOffset * u_intensity * 0.012;
  float r = texture(u_texture, uv + vec2(shift, 0.0)).r;
  float g = texture(u_texture, uv).g;
  float b = texture(u_texture, uv - vec2(shift, 0.0)).b;
  vec3 col = vec3(r, g, b);
  col = mix(col, texture(u_texture, uv + vec2(u_blur * 0.002, 0.0)).rgb, u_blur * 0.3);
  float scan = step(0.5, fract(uv.y * 300.0)) * u_scanLineOpacity * u_intensity;
  col -= scan;
  col += (hash(uv * 800.0 + u_time) - 0.5) * u_noiseAmount * u_intensity;
  float band = step(0.97, hash(vec2(floor(uv.y * 12.0), u_seed)));
  col *= 1.0 - band * u_intensity * 0.4;
  outColor = vec4(clamp(col, 0.0, 1.0), 1.0);
}
`;

export const VERTEX_PARTICLE_INSTANCED = `#version 300 es
precision highp float;
layout(location = 0) in vec2 a_corner;
layout(location = 1) in vec2 a_position;
layout(location = 2) in float a_size;
layout(location = 3) in float a_alpha;
uniform vec2 u_resolution;
out float v_alpha;
void main() {
  vec2 world = a_position + a_corner * a_size;
  vec2 ndc = (world / u_resolution) * 2.0 - 1.0;
  ndc.y = -ndc.y;
  gl_Position = vec4(ndc, 0.0, 1.0);
  v_alpha = a_alpha;
}
`;

export const FRAG_PARTICLE_INSTANCED = `#version 300 es
precision highp float;
in float v_alpha;
uniform vec3 u_color;
out vec4 outColor;
void main() {
  outColor = vec4(u_color, v_alpha);
}
`;
